import { useEffect, useMemo, useState } from 'react';
import { Plus, Search, Pencil, Trash2, Loader2, AlertCircle, Compass, ExternalLink, ShieldCheck } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from '@/components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AdminAPI } from '@/lib/admin-api';
import { toast } from 'sonner';

const CONFIDENCE_LEVELS = [
  { value: 'official', label: 'Official (issuer T&C)' },
  { value: 'verified', label: 'Verified (community confirmed)' },
  { value: 'experimental', label: 'Experimental (under test)' },
  { value: 'low', label: 'Low (anecdote — show with warning)' },
];

const SOURCE_TYPES = [
  { value: 'issuer_docs', label: 'Issuer docs' },
  { value: 'support_confirmed', label: 'Support confirmed' },
  { value: 'community_observed', label: 'Community observed' },
  { value: 'experiment', label: 'Experiment' },
  { value: 'reverse_engineered', label: 'Reverse-engineered' },
];

const emptyVoucherRoute = {
  title: '', instructions: '', suggested_card_id: '', suggested_card_label: '',
  portal_url: '', portal_name: '', effective_rate_pct: '', rate_uplift_pct: '', constraints: '',
};

const emptyForm = {
  merchant_slug: '', merchant_name: '', hostname_patterns: '', category: '', default_mcc: '',
  color: '', notes: '', is_active: true,
  voucher_route_enabled: false, voucher_route: { ...emptyVoucherRoute },
  provenance: {
    confidence_level: 'verified', source_type: 'community_observed',
    verification_status: 'verified', community_verified: true,
    observation_notes: '', source_url: '',
  },
};

function hintToForm(hint) {
  return {
    merchant_slug: hint.merchant_slug || '',
    merchant_name: hint.merchant_name || '',
    hostname_patterns: (hint.hostname_patterns || []).join(', '),
    category: hint.category || '',
    default_mcc: hint.default_mcc || '',
    color: hint.color || '',
    notes: hint.notes || '',
    is_active: !!hint.is_active,
    voucher_route_enabled: !!hint.voucher_route,
    voucher_route: hint.voucher_route ? {
      title: hint.voucher_route.title || '',
      instructions: hint.voucher_route.instructions || '',
      suggested_card_id: hint.voucher_route.suggested_card_id || '',
      suggested_card_label: hint.voucher_route.suggested_card_label || '',
      portal_url: hint.voucher_route.portal_url || '',
      portal_name: hint.voucher_route.portal_name || '',
      effective_rate_pct: hint.voucher_route.effective_rate_pct ?? '',
      rate_uplift_pct: hint.voucher_route.rate_uplift_pct ?? '',
      constraints: hint.voucher_route.constraints || '',
    } : { ...emptyVoucherRoute },
    provenance: hint.provenance ? {
      confidence_level: hint.provenance.confidence_level || 'verified',
      source_type: hint.provenance.source_type || 'community_observed',
      verification_status: hint.provenance.verification_status || 'verified',
      community_verified: !!hint.provenance.community_verified,
      observation_notes: hint.provenance.observation_notes || '',
      source_url: hint.provenance.source_url || '',
    } : emptyForm.provenance,
  };
}

function formToPayload(form) {
  const hostnames = form.hostname_patterns
    .split(/[\n,]/g)
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);

  const payload = {
    merchant_slug: form.merchant_slug.trim().toLowerCase(),
    merchant_name: form.merchant_name.trim(),
    hostname_patterns: hostnames,
    category: form.category.trim(),
    default_mcc: form.default_mcc.trim() || null,
    color: form.color.trim() || null,
    notes: form.notes.trim() || null,
    is_active: !!form.is_active,
    provenance: {
      confidence_level: form.provenance.confidence_level,
      source_type: form.provenance.source_type,
      verification_status: form.provenance.verification_status,
      community_verified: !!form.provenance.community_verified,
      observation_notes: form.provenance.observation_notes.trim() || null,
      source_url: form.provenance.source_url.trim() || null,
    },
  };
  if (form.voucher_route_enabled && form.voucher_route.title.trim()) {
    payload.voucher_route = {
      title: form.voucher_route.title.trim(),
      instructions: form.voucher_route.instructions.trim(),
      suggested_card_id: form.voucher_route.suggested_card_id.trim() || null,
      suggested_card_label: form.voucher_route.suggested_card_label.trim() || null,
      portal_url: form.voucher_route.portal_url.trim() || null,
      portal_name: form.voucher_route.portal_name.trim() || null,
      effective_rate_pct: form.voucher_route.effective_rate_pct === '' ? null : Number(form.voucher_route.effective_rate_pct),
      rate_uplift_pct: form.voucher_route.rate_uplift_pct === '' ? null : Number(form.voucher_route.rate_uplift_pct),
      constraints: form.voucher_route.constraints.trim() || null,
    };
  } else {
    payload.voucher_route = null;
  }
  return payload;
}

function confidenceBadge(level) {
  const map = {
    official: { cls: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30', label: 'Official' },
    verified: { cls: 'bg-sky-500/10 text-sky-600 border-sky-500/30', label: 'Verified' },
    experimental: { cls: 'bg-amber-500/10 text-amber-600 border-amber-500/30', label: 'Experimental' },
    low: { cls: 'bg-rose-500/10 text-rose-500 border-rose-500/30', label: 'Low confidence' },
  };
  const m = map[level] || map.verified;
  return <Badge className={`${m.cls} text-[10px]`}>{m.label}</Badge>;
}

export default function AdminRoutingHints() {
  const [hints, setHints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [activeTab, setActiveTab] = useState('details');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await AdminAPI.routingHints.list();
      setHints(data || []);
    } catch (e) {
      setError(e.message || 'Failed to load routing hints');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!q) return hints;
    const qq = q.toLowerCase();
    return hints.filter((h) => (
      `${h.merchant_name} ${h.merchant_slug} ${h.category}`.toLowerCase().includes(qq)
      || (h.hostname_patterns || []).some((p) => p.toLowerCase().includes(qq))
    ));
  }, [hints, q]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setActiveTab('details');
    setOpen(true);
  };

  const openEdit = (h) => {
    setEditing(h);
    setForm(hintToForm(h));
    setActiveTab('details');
    setOpen(true);
  };

  const submit = async () => {
    if (!form.merchant_slug || !form.merchant_name || !form.category) {
      toast.error('Slug, name, and category are required.');
      return;
    }
    if (!/^[a-z0-9-]+$/.test(form.merchant_slug)) {
      toast.error('Slug must be lowercase letters, numbers, and dashes only.');
      return;
    }
    setSaving(true);
    try {
      const payload = formToPayload(form);
      if (editing) {
        await AdminAPI.routingHints.update(editing.id, payload);
        toast.success(`Routing hint for “${form.merchant_name}” updated`);
      } else {
        await AdminAPI.routingHints.create(payload);
        toast.success(`Routing hint for “${form.merchant_name}” created`);
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!deleteTarget) return;
    try {
      await AdminAPI.routingHints.remove(deleteTarget.id);
      toast.success('Routing hint deleted');
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(e?.message || 'Delete failed');
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-routing-hints-page">
      <div className="flex items-start sm:items-center justify-between gap-3 flex-col sm:flex-row">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-accent font-medium">Intelligence</div>
          <h1 className="mt-1.5 font-display text-xl sm:text-2xl font-semibold tracking-tight">Routing hints</h1>
          <p className="mt-1 text-xs text-muted-foreground">
            {hints.length} merchant{hints.length === 1 ? '' : 's'} curated for the Chrome extension. Hints are <b>observed intelligence</b> &mdash; tag confidence so users see the right trust signal.
          </p>
        </div>
        <Button onClick={openCreate} size="sm" className="gap-1.5" data-testid="admin-routing-hints-create-button">
          <Plus className="h-4 w-4" /> New hint
        </Button>
      </div>

      <Card className="p-3 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by merchant name, slug, hostname, or category..."
            className="pl-9 h-9 border-transparent bg-[hsl(var(--surface-2))]"
            data-testid="admin-routing-hints-search-input"
          />
        </div>
      </Card>

      {error && (
        <Card className="p-3 border-rose-500/30 bg-rose-500/5 flex items-start gap-2 text-sm text-rose-700 dark:text-rose-300">
          <AlertCircle className="h-4 w-4 mt-0.5" /><span>{error}</span>
          <Button size="sm" variant="ghost" className="ml-auto" onClick={load}>Retry</Button>
        </Card>
      )}

      <Card className="overflow-x-auto">
        <Table data-testid="admin-routing-hints-table">
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-[34%]">Merchant</TableHead>
              <TableHead className="hidden lg:table-cell">Hostnames</TableHead>
              <TableHead className="hidden md:table-cell">Category</TableHead>
              <TableHead className="hidden sm:table-cell">Smart route</TableHead>
              <TableHead className="hidden sm:table-cell">Confidence</TableHead>
              <TableHead className="hidden sm:table-cell">Status</TableHead>
              <TableHead className="w-[110px] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={7}><Skeleton className="h-6 w-full" /></TableCell>
                </TableRow>
              ))
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="py-12 text-center text-muted-foreground">
                  <Compass className="h-6 w-6 mx-auto opacity-50 mb-2" />
                  <div className="text-sm">No hints match.</div>
                  <Button size="sm" variant="outline" className="mt-3" onClick={openCreate}>Add a hint</Button>
                </TableCell>
              </TableRow>
            ) : filtered.map((h) => (
              <TableRow key={h.id} className="h-10" data-testid={`admin-routing-hints-row-${h.id}`}>
                <TableCell className="py-2">
                  <div className="flex items-center gap-2.5">
                    <div className="h-7 w-7 rounded-md grid place-items-center text-[10px] font-display font-semibold text-white shrink-0" style={{ background: h.color || '#475569' }}>
                      {(h.merchant_name || '').split(' ').map((w) => w[0]).slice(0, 2).join('')}
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{h.merchant_name}</div>
                      <div className="text-[10px] text-muted-foreground font-mono-num truncate">{h.merchant_slug}</div>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="hidden lg:table-cell py-2">
                  <div className="text-xs text-muted-foreground font-mono-num truncate max-w-[220px]">
                    {(h.hostname_patterns || []).slice(0, 2).join(', ')}{(h.hostname_patterns || []).length > 2 ? ` +${h.hostname_patterns.length - 2}` : ''}
                  </div>
                </TableCell>
                <TableCell className="hidden md:table-cell py-2">
                  <Badge variant="outline" className="text-[10px]">{h.category}</Badge>
                </TableCell>
                <TableCell className="hidden sm:table-cell py-2">
                  {h.voucher_route ? (
                    <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/30 text-[10px]">
                      {h.voucher_route.rate_uplift_pct ? `+${h.voucher_route.rate_uplift_pct}%` : 'Yes'}
                    </Badge>
                  ) : (
                    <span className="text-[11px] text-muted-foreground">&mdash;</span>
                  )}
                </TableCell>
                <TableCell className="hidden sm:table-cell py-2">
                  {confidenceBadge(h.provenance?.confidence_level)}
                </TableCell>
                <TableCell className="hidden sm:table-cell py-2">
                  {h.is_active
                    ? <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/30 text-[10px]">Active</Badge>
                    : <Badge variant="outline" className="text-[10px] text-muted-foreground">Inactive</Badge>}
                </TableCell>
                <TableCell className="py-2 text-right">
                  <div className="inline-flex items-center gap-1">
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(h)} data-testid={`admin-routing-hints-edit-${h.id}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-rose-500" onClick={() => setDeleteTarget(h)} data-testid={`admin-routing-hints-delete-${h.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Create / Edit Drawer */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full sm:max-w-xl p-0" data-testid="admin-routing-hints-drawer">
          <div className="p-5 border-b border-border">
            <SheetHeader className="space-y-1">
              <SheetTitle className="font-display text-lg">
                {editing ? `Edit hint — ${editing.merchant_name}` : 'New routing hint'}
              </SheetTitle>
              <SheetDescription className="text-xs">
                Hints are <b>observed intelligence</b>. The deterministic engine is still the source of truth for any direct checkout math.
              </SheetDescription>
            </SheetHeader>
          </div>

          <div className="px-5 pt-3 border-b border-border">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="bg-transparent p-0 h-auto gap-2">
                <TabsTrigger value="details" className="data-[state=active]:bg-[hsl(var(--surface-2))] px-3 py-1.5 text-xs">Details</TabsTrigger>
                <TabsTrigger value="voucher" className="data-[state=active]:bg-[hsl(var(--surface-2))] px-3 py-1.5 text-xs">Smart route</TabsTrigger>
                <TabsTrigger value="provenance" className="data-[state=active]:bg-[hsl(var(--surface-2))] px-3 py-1.5 text-xs">Provenance</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          <div className="p-5 space-y-4 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 260px)' }}>
            {activeTab === 'details' && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Slug <span className="text-rose-500">*</span></Label>
                    <Input
                      value={form.merchant_slug}
                      onChange={(e) => setForm((f) => ({ ...f, merchant_slug: e.target.value }))}
                      placeholder="amazon-in"
                      className="mt-1 h-9 font-mono-num"
                      data-testid="admin-routing-hints-form-slug"
                      disabled={!!editing}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Display name <span className="text-rose-500">*</span></Label>
                    <Input
                      value={form.merchant_name}
                      onChange={(e) => setForm((f) => ({ ...f, merchant_name: e.target.value }))}
                      placeholder="Amazon India"
                      className="mt-1 h-9"
                      data-testid="admin-routing-hints-form-name"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Hostname patterns (comma- or newline-separated)</Label>
                  <Textarea
                    value={form.hostname_patterns}
                    onChange={(e) => setForm((f) => ({ ...f, hostname_patterns: e.target.value }))}
                    rows={3}
                    placeholder="amazon.in, www.amazon.in, m.amazon.in"
                    className="mt-1 font-mono-num"
                    data-testid="admin-routing-hints-form-hostnames"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Category <span className="text-rose-500">*</span></Label>
                    <Input
                      value={form.category}
                      onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                      placeholder="Online Shopping"
                      className="mt-1 h-9"
                      data-testid="admin-routing-hints-form-category"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Default MCC</Label>
                    <Input
                      value={form.default_mcc}
                      onChange={(e) => setForm((f) => ({ ...f, default_mcc: e.target.value }))}
                      placeholder="5399"
                      className="mt-1 h-9 font-mono-num"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Brand color (hex)</Label>
                    <div className="mt-1 flex items-center gap-2">
                      <Input
                        value={form.color}
                        onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
                        placeholder="#FF9900"
                        className="h-9 font-mono-num"
                      />
                      {form.color && /^#[0-9a-fA-F]{3,8}$/.test(form.color) && (
                        <div className="h-9 w-9 rounded border border-border" style={{ background: form.color }} />
                      )}
                    </div>
                  </div>
                  <div className="flex items-end">
                    <div className="w-full flex items-center justify-between rounded-lg border border-border bg-[hsl(var(--surface-2))] px-3 py-2">
                      <Label htmlFor="rh-active" className="text-sm">Active</Label>
                      <Switch
                        id="rh-active"
                        checked={form.is_active}
                        onCheckedChange={(v) => setForm((f) => ({ ...f, is_active: v }))}
                        data-testid="admin-routing-hints-form-active"
                      />
                    </div>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Notes (admin-only context)</Label>
                  <Textarea
                    value={form.notes}
                    onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value.slice(0, 400) }))}
                    rows={3}
                    placeholder="MCC inconsistency on Zomato — sometimes posts as 5814..."
                    className="mt-1"
                  />
                  <div className="mt-1 text-[10px] text-muted-foreground text-right">{form.notes.length}/400</div>
                </div>
              </div>
            )}

            {activeTab === 'voucher' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between rounded-lg border border-border bg-[hsl(var(--surface-2))] px-3 py-2.5">
                  <div>
                    <Label htmlFor="vr-enabled" className="text-sm font-medium">Smart routing path</Label>
                    <div className="text-[10px] text-muted-foreground">Toggle off if direct checkout is the optimal path.</div>
                  </div>
                  <Switch
                    id="vr-enabled"
                    checked={form.voucher_route_enabled}
                    onCheckedChange={(v) => setForm((f) => ({ ...f, voucher_route_enabled: v }))}
                    data-testid="admin-routing-hints-form-voucher-enabled"
                  />
                </div>
                {form.voucher_route_enabled && (
                  <>
                    <div>
                      <Label className="text-xs">Title <span className="text-rose-500">*</span></Label>
                      <Input
                        value={form.voucher_route.title}
                        onChange={(e) => setForm((f) => ({ ...f, voucher_route: { ...f.voucher_route, title: e.target.value } }))}
                        placeholder="Buy an Amazon Pay voucher via HDFC SmartBuy"
                        className="mt-1 h-9"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Instructions</Label>
                      <Textarea
                        value={form.voucher_route.instructions}
                        onChange={(e) => setForm((f) => ({ ...f, voucher_route: { ...f.voucher_route, instructions: e.target.value } }))}
                        rows={4}
                        placeholder="Open hdfcsmartbuy.com → Vouchers → Amazon..."
                        className="mt-1"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Suggested card ID</Label>
                        <Input
                          value={form.voucher_route.suggested_card_id}
                          onChange={(e) => setForm((f) => ({ ...f, voucher_route: { ...f.voucher_route, suggested_card_id: e.target.value } }))}
                          placeholder="card-..."
                          className="mt-1 h-9 font-mono-num"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Suggested card label (fallback)</Label>
                        <Input
                          value={form.voucher_route.suggested_card_label}
                          onChange={(e) => setForm((f) => ({ ...f, voucher_route: { ...f.voucher_route, suggested_card_label: e.target.value } }))}
                          placeholder="HDFC BizBlack"
                          className="mt-1 h-9"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Portal URL</Label>
                        <Input
                          value={form.voucher_route.portal_url}
                          onChange={(e) => setForm((f) => ({ ...f, voucher_route: { ...f.voucher_route, portal_url: e.target.value } }))}
                          placeholder="https://www.hdfcsmartbuy.com"
                          className="mt-1 h-9"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Portal name</Label>
                        <Input
                          value={form.voucher_route.portal_name}
                          onChange={(e) => setForm((f) => ({ ...f, voucher_route: { ...f.voucher_route, portal_name: e.target.value } }))}
                          placeholder="HDFC SmartBuy"
                          className="mt-1 h-9"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Effective rate (%)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={form.voucher_route.effective_rate_pct}
                          onChange={(e) => setForm((f) => ({ ...f, voucher_route: { ...f.voucher_route, effective_rate_pct: e.target.value } }))}
                          placeholder="3.33"
                          className="mt-1 h-9 font-mono-num"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Uplift vs direct (%)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={form.voucher_route.rate_uplift_pct}
                          onChange={(e) => setForm((f) => ({ ...f, voucher_route: { ...f.voucher_route, rate_uplift_pct: e.target.value } }))}
                          placeholder="1.66"
                          className="mt-1 h-9 font-mono-num"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Constraints</Label>
                      <Textarea
                        value={form.voucher_route.constraints}
                        onChange={(e) => setForm((f) => ({ ...f, voucher_route: { ...f.voucher_route, constraints: e.target.value } }))}
                        rows={2}
                        placeholder="Vouchers up to ₹10,000/day. Cannot stack with fuel category..."
                        className="mt-1"
                      />
                    </div>
                  </>
                )}
              </div>
            )}

            {activeTab === 'provenance' && (
              <div className="space-y-4">
                <div className="rounded-lg border border-border bg-[hsl(var(--surface-2))] p-3 text-xs text-muted-foreground flex items-start gap-2">
                  <ShieldCheck className="h-4 w-4 text-accent mt-0.5 shrink-0" />
                  <div>Tag the trust level so the extension can surface "Observed" vs "Issuer-confirmed" appropriately. The web app and extension never use provenance metadata to compute money &mdash; only to communicate certainty.</div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Confidence</Label>
                    <Select
                      value={form.provenance.confidence_level}
                      onValueChange={(v) => setForm((f) => ({ ...f, provenance: { ...f.provenance, confidence_level: v } }))}
                    >
                      <SelectTrigger className="mt-1 h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>{CONFIDENCE_LEVELS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Source type</Label>
                    <Select
                      value={form.provenance.source_type}
                      onValueChange={(v) => setForm((f) => ({ ...f, provenance: { ...f.provenance, source_type: v } }))}
                    >
                      <SelectTrigger className="mt-1 h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>{SOURCE_TYPES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Source URL</Label>
                  <Input
                    value={form.provenance.source_url}
                    onChange={(e) => setForm((f) => ({ ...f, provenance: { ...f.provenance, source_url: e.target.value } }))}
                    placeholder="https://www.hdfcsmartbuy.com/terms"
                    className="mt-1 h-9"
                  />
                </div>
                <div>
                  <Label className="text-xs">Observation notes</Label>
                  <Textarea
                    value={form.provenance.observation_notes}
                    onChange={(e) => setForm((f) => ({ ...f, provenance: { ...f.provenance, observation_notes: e.target.value } }))}
                    rows={3}
                    placeholder="Widely confirmed in CCG community. Subject to issuer policy changes."
                    className="mt-1"
                  />
                </div>
                <div className="flex items-center justify-between rounded-lg border border-border bg-[hsl(var(--surface-2))] px-3 py-2.5">
                  <div>
                    <Label htmlFor="rh-community" className="text-sm">Community-verified</Label>
                    <div className="text-[10px] text-muted-foreground">Multiple independent reports confirm this routing path.</div>
                  </div>
                  <Switch
                    id="rh-community"
                    checked={form.provenance.community_verified}
                    onCheckedChange={(v) => setForm((f) => ({ ...f, provenance: { ...f.provenance, community_verified: v } }))}
                  />
                </div>
              </div>
            )}
          </div>

          <SheetFooter className="p-4 border-t border-border">
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving} data-testid="admin-routing-hints-form-cancel">Cancel</Button>
            <Button onClick={submit} disabled={saving} className="gap-1.5" data-testid="admin-routing-hints-form-save">
              {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {editing ? 'Save changes' : 'Create hint'}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete hint for &ldquo;{deleteTarget?.merchant_name}&rdquo;?</AlertDialogTitle>
            <AlertDialogDescription>
              The Chrome extension will stop showing routing suggestions for this merchant. You can re-create it later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-routing-hints-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-rose-600 hover:bg-rose-700 focus:ring-rose-500" data-testid="admin-routing-hints-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
